/**
 * Persona-related localStorage service
 * Handles all persona data persistence in localStorage
 */

const STORAGE_KEYS = {
  UNAUTH_SEARCH_TEXT: "unauthUserSearchText",
  GENERATED_PERSONA_DATA: "generatedPersonaData",
  PENDING_PERSONA_DATA: "pendingPersonaData",
};

/**
 * Store unauthenticated user's search text
 * @param {string} searchText - The search text to store
 */
export const storeUnauthSearchText = (searchText) => {
  localStorage.setItem(STORAGE_KEYS.UNAUTH_SEARCH_TEXT, searchText);
};

/**
 * Get stored unauthenticated user's search text
 * @returns {string|null} The stored search text or null
 */
export const getUnauthSearchText = () => {
  return localStorage.getItem(STORAGE_KEYS.UNAUTH_SEARCH_TEXT);
};

/**
 * Clear unauthenticated user's search text
 */
export const clearUnauthSearchText = () => {
  localStorage.removeItem(STORAGE_KEYS.UNAUTH_SEARCH_TEXT);
};

/**
 * Store generated persona data
 * @param {Object} personaData - The persona data to store
 */
export const storeGeneratedPersonaData = (personaData) => {
  localStorage.setItem(
    STORAGE_KEYS.GENERATED_PERSONA_DATA,
    JSON.stringify(personaData)
  );
};

/**
 * Get stored generated persona data
 * @returns {Object|null} The stored persona data or null
 */
export const getGeneratedPersonaData = () => {
  const data = localStorage.getItem(STORAGE_KEYS.GENERATED_PERSONA_DATA);
  return data ? JSON.parse(data) : null;
};

/**
 * Clear generated persona data
 */
export const clearGeneratedPersonaData = () => {
  localStorage.removeItem(STORAGE_KEYS.GENERATED_PERSONA_DATA);
};

/**
 * Store pending persona data (for unauthenticated users)
 * @param {Object} personaData - The persona data to store
 */
export const storePendingPersonaData = (personaData) => {
  localStorage.setItem(
    STORAGE_KEYS.PENDING_PERSONA_DATA,
    JSON.stringify(personaData)
  );
};

/**
 * Get stored pending persona data
 * @returns {Object|null} The stored persona data or null
 */
export const getPendingPersonaData = () => {
  const data = localStorage.getItem(STORAGE_KEYS.PENDING_PERSONA_DATA);
  return data ? JSON.parse(data) : null;
};

/**
 * Clear pending persona data
 */
export const clearPendingPersonaData = () => {
  localStorage.removeItem(STORAGE_KEYS.PENDING_PERSONA_DATA);
};

/**
 * Store selected persona ID
 * @param {string} personaId - The persona ID to store
 */
export const storeSelectedPersonaId = (personaId) => {
  if (personaId) {
    localStorage.setItem("selectedPersonaId", personaId);
  } else {
    localStorage.removeItem("selectedPersonaId");
  }
};

/**
 * Get stored selected persona ID
 * @returns {string|null} The stored persona ID or null
 */
export const getSelectedPersonaId = () => {
  return localStorage.getItem("selectedPersonaId");
};

/**
 * Clear selected persona ID
 */
export const clearSelectedPersonaId = () => {
  localStorage.removeItem("selectedPersonaId");
};

/**
 * Store suggested persona dismissed state
 * @param {boolean} isDismissed - Whether the suggested persona was dismissed
 */
export const storeSuggestedPersonaDismissed = (isDismissed) => {
  if (isDismissed) {
    localStorage.setItem("suggestedPersonaDismissed", "true");
  } else {
    localStorage.removeItem("suggestedPersonaDismissed");
  }
};

/**
 * Get suggested persona dismissed state
 * @returns {boolean} Whether the suggested persona was dismissed
 */
export const getSuggestedPersonaDismissed = () => {
  return localStorage.getItem("suggestedPersonaDismissed") === "true";
};

/**
 * Clear suggested persona dismissed state
 */
export const clearSuggestedPersonaDismissed = () => {
  localStorage.removeItem("suggestedPersonaDismissed");
};

/**
 * Store show full persona state
 * @param {boolean} showFull - Whether to show the full persona or just the dimmed avatar
 */
export const storeShowFullPersona = (showFull) => {
  localStorage.setItem("showFullPersona", showFull ? "true" : "false");
};

/**
 * Get show full persona state
 * @returns {boolean} Whether to show the full persona (defaults to true)
 */
export const getShowFullPersona = () => {
  const stored = localStorage.getItem("showFullPersona");
  return stored === null ? true : stored === "true";
};

/**
 * Clear show full persona state
 */
export const clearShowFullPersona = () => {
  localStorage.removeItem("showFullPersona");
};

/**
 * Clear all persona-related data from localStorage
 */
export const clearAllPersonaData = () => {
  clearUnauthSearchText();
  clearGeneratedPersonaData();
  clearPendingPersonaData();
  clearSelectedPersonaId();
  clearSuggestedPersonaDismissed();
  clearShowFullPersona();
};
